/**
 * The dashboard's PAGES — the unit per-staff visibility is granted in.
 *
 * ONE registry, imported by both trees: the backend gates `/api/*` on it and
 * the SPA filters its nav and blocks its routes on it. A second list would be
 * a permission that disagrees with itself — the nav hiding a page the API
 * still answers, or the reverse, and neither failure announces itself.
 *
 * WHY A GRANT-LIST AND NOT A DENY-LIST. `staff_allowlist.visible_pages` is
 * "these pages, exactly" rather than "everything except these". A deny-list
 * would silently GRANT every page added after the restriction was written; a
 * restricted person would wake up one morning able to see a new Payroll page
 * because it did not exist when someone unticked things. A grant-list gets
 * that case right by defaulting to no.
 *
 * NULL vs a list: `visible_pages IS NULL` means UNRESTRICTED — the state every
 * row is in before anyone touches it, and the reason migration 178 changes no
 * behaviour on deploy. Only a non-null array restricts.
 *
 * ## What this gates, and what it does not
 *
 * Page access decides what a person can SEE. What they can DO stays governed
 * by the actor matrix and the `admin` tier at the dispatch chokepoint
 * (invariant 5) — `/api/dispatch/*` is deliberately {@link UNGATED_API}.
 * Gating dispatch by page would need a second action-to-page registry, and a
 * registry that has to be kept in step with 190+ actions by hand is the kind
 * that rots into a lie. So: hiding `/payroll` removes the page, its nav entry
 * and its board endpoint; it does NOT stop someone who knows an action name
 * from dispatching it. Action-level scoping is the `role` claim's job, which
 * `server.ts` has parsed and deliberately not consumed since phase 11.
 *
 * Say that out loud in the editor rather than implying a stronger boundary
 * than exists.
 */

/** A page a staff member's access can be granted or withheld. */
export interface DashboardPage {
  /** Stable id — what is stored in `visible_pages`. NEVER rename one. */
  readonly id: string;
  /** Human label, for the tick-list. */
  readonly label: string;
  /** Grouping for the tick-list, mirroring how the nav is grouped. */
  readonly group: string;
  /**
   * SPA route patterns this page owns, including its detail children —
   * `:param` matches one path segment. A detail route follows its parent
   * because they are one feature to an operator: nobody means "you may see
   * the members list but not a member".
   */
  readonly routes: ReadonlyArray<string>;
  /**
   * `/api` paths this page owns. A trailing `/` means prefix; anything else is
   * matched exactly. Longest match wins, so `/api/data-counts` and `/api/data/`
   * can coexist.
   *
   * Ownership is NOT exclusive: one URL may be claimed by several pages
   * through different entries — an exact entry here alongside another page's
   * prefix. The gate allows a request when ANY claiming page is granted,
   * because a URL two pages read through is one feature reachable from both,
   * and gating it behind exactly one of them 403s the other (the standalone
   * Members page reading `/api/data/members`, Eladio 2026-08-19 — the same
   * class as the `/api/saga-runs/` poll, 2026-08-18).
   */
  readonly api: ReadonlyArray<string>;
  /**
   * Always reachable, whatever `visible_pages` says. Home only: it is where a
   * blocked route redirects to and where a fail-closed viewer lands, so a
   * restriction that could remove it would be a redirect loop into a blank
   * product.
   */
  readonly alwaysAllowed?: true;
}

export const DASHBOARD_PAGES: ReadonlyArray<DashboardPage> = [
  {
    id: "home",
    label: "Home",
    group: "Overview",
    routes: ["/"],
    api: ["/api/home-board"],
    alwaysAllowed: true,
  },
  {
    id: "calendar",
    label: "Calendar",
    group: "Day to day",
    routes: ["/calendar"],
    api: [
      "/api/calendar",
      "/api/day-context",
      "/api/slots/parse-bulk",
      "/api/availability/parse",
    ],
  },
  {
    id: "members",
    label: "Members",
    group: "Day to day",
    routes: ["/members", "/members/:id"],
    // The standalone `/members` page IS the data-browser members table
    // (`pages/Data.tsx` → `Members`), so it reads rows through
    // `/api/data/members` and row-deletes through `/api/data-row/members` —
    // both of which the `data` page also claims by prefix. Co-owned on
    // purpose: either grant suffices (see the `api` field doc). Granting
    // Members does NOT open the rest of the browser — every other entity
    // stays behind the `data` prefix.
    api: ["/api/members/", "/api/data/members", "/api/data-row/members"],
  },
  {
    id: "trainers",
    label: "Trainer pages",
    group: "Day to day",
    // No nav entry of its own — reached from Calendar and Members. Still a
    // page in its own right here, because "can this person open a trainer's
    // day, with every client's name on it" is a real question an operator may
    // want to answer no to.
    // `POST /api/trainers/:id/pay-link` (minting a trainer's provisional-pay
    // link) needs no entry of its own — it sits under the `/api/trainers/`
    // prefix already claimed here, which is also where it belongs: it is an
    // operation ON a trainer, and "may this person open a trainer's page" is
    // exactly the question that should decide it.
    routes: ["/trainers/:id", "/trainers/:id/today"],
    api: ["/api/trainers/", "/api/today"],
  },
  {
    id: "tasks",
    label: "Tasks",
    group: "Work queues",
    routes: ["/tasks", "/tasks/:id"],
    api: ["/api/tasks-board", "/api/tasks/"],
  },
  {
    id: "pr",
    label: "PR",
    group: "Work queues",
    routes: ["/pr", "/pr/people/:id", "/outreach", "/outreach/:id"],
    api: ["/api/pr-board", "/api/outreach/"],
  },
  {
    id: "inbox",
    label: "Inbox",
    group: "Work queues",
    routes: ["/inbox", "/inbox/threads/:id"],
    api: [
      "/api/inbox-board",
      "/api/inbox-chats",
      "/api/inbox-chats/",
      "/api/inbox/threads/",
      "/api/instagram-comments",
    ],
  },
  {
    id: "decisions",
    label: "Decisions",
    group: "Work queues",
    routes: ["/decisions"],
    api: ["/api/decisions-board", "/api/decisions/"],
  },
  {
    id: "flows",
    label: "Flows",
    group: "Work queues",
    routes: ["/flows", "/flows/:flow_name/edit"],
    api: ["/api/flows-stats", "/api/flows/", "/api/flow-members"],
  },
  {
    id: "automations",
    label: "Automations",
    group: "Work queues",
    routes: ["/automations"],
    // `/api/saga-runs/` (the per-run completion poll) is deliberately NOT
    // here — see UNGATED_API. Only the run LIST is an Automations read.
    api: ["/api/sagas"],
  },
  {
    id: "agents",
    label: "Agents",
    group: "Work queues",
    routes: ["/agents"],
    api: ["/api/agents", "/api/agents/activity"],
  },
  {
    id: "analytics",
    label: "Analytics",
    group: "Read and configure",
    routes: ["/analytics"],
    api: ["/api/analytics"],
  },
  {
    id: "assets",
    label: "Brand assets",
    group: "Read and configure",
    // The brand-assets gallery + uploader (migration 195). No endpoint of its
    // own — every read and write goes through `/api/dispatch/`
    // (list_brand_assets / create_brand_asset_upload / finalize_brand_asset /
    // update_brand_asset / delete_brand_asset), which is ungated by design
    // (see UNGATED_API). Hiding this page removes the page + its nav entry,
    // not the underlying actions — same posture as `/errors` and `/reports`.
    routes: ["/assets"],
    api: [],
  },
  {
    id: "data",
    label: "Data browser",
    group: "Read and configure",
    routes: ["/data", "/data/:entity"],
    api: ["/api/data/", "/api/data-counts", "/api/data-row/"],
  },
  {
    id: "config",
    label: "Config",
    group: "Read and configure",
    // The page that edits page access. See `LOCKOUT_GUARD_PAGE_ID`.
    routes: ["/config", "/config/:slug"],
    api: [
      "/api/studio-config/",
      "/api/studio-secrets/",
      "/api/staff-allowlist/",
    ],
  },
  {
    id: "rota",
    label: "Rota",
    group: "Periodic",
    routes: ["/rota"],
    api: ["/api/rota-board"],
  },
  {
    id: "payroll",
    label: "Payroll",
    group: "Periodic",
    routes: ["/payroll"],
    api: ["/api/payroll-board"],
  },
  {
    id: "reports",
    label: "Reports",
    group: "Periodic",
    routes: ["/reports"],
    // No endpoint of its own — every figure comes through `/api/dispatch/`,
    // which is ungated by design (see the header). Hiding this page therefore
    // removes the page and its nav entry, not the underlying reads.
    api: [],
  },
  {
    id: "status",
    label: "Status",
    group: "Operator tools",
    routes: ["/status"],
    api: ["/api/status", "/api/status/run-test"],
  },
  {
    id: "newsletter",
    label: "Newsletter",
    group: "Operator tools",
    // The WhatsApp article-and-booking send. No endpoint of its own — the
    // preview AND the send both go through `/api/dispatch/`
    // (`send_whatsapp_newsletter`), which is ungated by design (see
    // UNGATED_API), so hiding this page removes the page and its nav entry,
    // not the ability to dispatch. Same posture as `/errors` and `/reports`.
    //
    // The action itself is the real gate: `human:staff` + `system:cli` only —
    // no agent, and deliberately no cron or saga, so nothing can schedule a
    // send to the membership.
    routes: ["/newsletter"],
    api: [],
  },
  {
    id: "errors",
    label: "Errors",
    group: "Operator tools",
    // The error-triage board (migration 187). No endpoint of its own — the list
    // + the human disposition both go through `/api/dispatch/`
    // (list_error_incidents / update_error_incident), which is ungated by design
    // (see UNGATED_API). Hiding this page removes the page + its nav entry, not
    // the underlying reads — same posture as `/reports`.
    routes: ["/errors", "/errors/:id"],
    api: [],
  },
  {
    id: "email-templates",
    label: "Emails",
    group: "Operator tools",
    routes: ["/email-templates", "/email-templates/:kind/edit"],
    api: ["/api/email-templates", "/api/email-templates/"],
  },
  {
    id: "efficiency",
    label: "Efficiency",
    group: "Operator tools",
    routes: ["/efficiency", "/efficiency/suggestions", "/efficiency/knowledge"],
    api: ["/api/efficiency", "/api/efficiency/judge", "/api/efficiency/suggestions"],
  },
  {
    id: "preload",
    label: "Preload",
    group: "Operator tools",
    routes: ["/preload"],
    api: ["/api/preload"],
  },
  {
    id: "catalog",
    label: "Catalog",
    group: "Operator tools",
    routes: ["/catalog"],
    // `/api/catalog` is NOT here — it is ungated. It is action SCHEMA, read by
    // every action modal on every page, so owning it would break Members the
    // moment someone lost Catalog.
    api: [],
  },
  {
    id: "cli",
    label: "CLI",
    group: "Operator tools",
    routes: ["/cli"],
    api: [],
  },
  {
    id: "mcp",
    label: "MCP",
    group: "Operator tools",
    routes: ["/mcp"],
    // 2026-08-24 — the OAuth-clients panel: whoever may see /mcp may manage
    // the MCP credentials it describes (list / create / edit scopes /
    // revoke). Restricting someone's `visible_pages` away from `mcp`
    // therefore also closes the permission-editing endpoint for them.
    api: ["/api/oauth-clients/"],
  },
  {
    id: "whatsapp-signup",
    label: "WhatsApp signup",
    group: "Operator tools",
    routes: ["/whatsapp-signup"],
    // 2026-08-25 — the Embedded Signup coexistence-onboarding page. Its
    // endpoint family (GET popup constants / POST code exchange) rotates
    // WhatsApp credentials, so whoever may see the page may run the flow;
    // restricting the page away also closes the exchange endpoint.
    api: ["/api/whatsapp-signup/"],
  },
];

/**
 * The page whose loss is unrecoverable in the UI, because it is the page that
 * edits page access. `edit_staff_allowlist` refuses a save that would leave
 * NOBODY able to reach it.
 */
export const LOCKOUT_GUARD_PAGE_ID = "config";

/**
 * `/api` paths NO page owns, each with the reason it cannot be owned. The
 * completeness test requires every `/api` literal in `server.ts` to be either
 * claimed by a page or listed here — a new endpoint that is neither fails the
 * build rather than defaulting to reachable.
 */
export const UNGATED_API: ReadonlyArray<{
  readonly path: string;
  readonly why: string;
}> = [
  {
    path: "/api/whoami",
    why: "The bootstrap call that TELLS the SPA its page set. Gating it would make the answer depend on itself.",
  },
  {
    path: "/api/catalog",
    why: "Action schemas, read by every action modal on every page. Not business data, and owning it would break unrelated pages.",
  },
  {
    path: "/api/dispatch/",
    why: "Canonical-action dispatch. Governed by the actor matrix + admin tier at the chokepoint (invariant 5); a page-keyed gate here would need a second action-to-page registry that could only rot.",
  },
  {
    path: "/api/saga-runs/",
    why: "The completion poll for a run the caller just dispatched. Saga-backed modals on MANY pages poll it after an ungated /api/dispatch/ 202, so whoever may dispatch must be able to observe the terminal state — gating it behind one page made every booking succeed while rendering as a 60s timeout for restricted staff (Eladio, 2026-08-18). The handler verifies its own JWT and reads scheduled_actions under RLS; the response is run-status metadata only.",
  },
  {
    path: "/api/lookups/",
    why: "Generic typeahead behind modals on Calendar, Members and several components at once. No single page owns it.",
  },
  {
    path: "/api/dashboard-layout",
    why: "The viewer's OWN card layout for a page they are already on. A self-service preference, not a data read.",
  },
  {
    path: "/api/login-link",
    why: "Pre-auth by definition — the caller is ASKING to sign in. Non-enumeration + rate limits live in StaffLoginLinkService, and the answer is uniform regardless of whether the identifier maps to anyone.",
  },
  {
    path: "/api/login-link/verify",
    why: "Pre-auth sibling of /api/login-link — exchanges the 8-digit code for a session; guess caps in the service.",
  },
  {
    path: "/api/trainer-pay/",
    why: "A trainer's own provisional-pay read, reached from a signed link (`trainer-pay-tokens.ts`). There is no viewer to resolve — trainers have no login and no `staff_allowlist` row — so the page gate has nothing to key on. The HMAC IS the auth: the token carries the studio + trainer in its signed payload, the handler scopes the read to exactly that trainer, and any token failure answers a uniform 403. Deliberately NOT reachable with a staff session either; the minting half, /api/trainer-pay-link, is the gated one.",
  },
  {
    path: "/api/errors/ingest/",
    why: "CI + Railway failure ingress (migration 187 · Part E). Its caller is a GitHub Actions step or the Railway poller, not a staff viewer — there is no login and no staff_allowlist row to key a page gate on. A SHARED SECRET header IS the auth (constant-time compared in the handler), and the studio is resolved from the reserved `platform` slug in the PATH, never a payload field (invariant 4). Deliberately NOT reachable with a staff session. Distinct from the `/errors` page's own reads, which ride the ungated /api/dispatch/.",
  },
  {
    path: "/api/dev/reset",
    why: "Dev-only BY DEFAULT — but NOT dev-only in this deployment, and the exemption must be read with that in mind. `bootstrap.ts` wires `deps.devReset` when `NODE_ENV !== 'production'` OR `LAYER_ALLOW_DEV_RESET === 'true'`, and production currently sets that flag, so the route is LIVE on the production dashboard rather than 404ing. What stands in front of it is `verifyJwt` alone: no studio check, no `role` check, no page gate. `runDevReset` then runs on the RLS-bypassing admin pool with no studio predicate (bare TRUNCATE + `DELETE FROM members`), so it is cross-tenant — any authenticated staff JWT for ANY studio wipes EVERY studio. That is survivable only while one tenant exists; a second staff identity makes this a second key to the first tenant's data. Gating it was declined on 2026-09-09 on the single-tenant premise. Re-open that decision BEFORE adding a second studio, not after.",
  },
  {
    path: "/api/dev/purge-members",
    why: "Flag-gated sibling of /api/dev/reset, wired ONLY under LAYER_ALLOW_MEMBER_PURGE=true (unset in production → 404). Studio-SCOPED by construction — the studio comes from the verified JWT's claim and every DELETE is predicated on it — so it carries none of the reset's cross-tenant hazard and needs no tenant gate. Same auth posture as the reset (any valid staff JWT, typed confirmation); page-gating it would need a page that owns 'delete everything', which none does.",
  },
  {
    path: "/api/dev/fast-forward",
    why: "Same wiring as /api/dev/reset (`deps.devReset` gates both), so the same caveat applies: present in production whenever LAYER_ALLOW_DEV_RESET is set. Far lower blast radius — it toggles a per-studio marketing_flow_fast_forward config key rather than deleting rows.",
  },
];

/**
 * SPA routes no page owns. `/login` is pre-auth; the catch-all is a redirect.
 */
export const UNGATED_ROUTES: ReadonlyArray<{
  readonly route: string;
  readonly why: string;
}> = [
  { route: "/login", why: "Pre-auth — there is no viewer to resolve yet." },
  {
    route: "/trainer-pay/:token",
    why: "The trainer's own provisional-pay page. Its viewer is a TRAINER, who has no dashboard login and no staff_allowlist row to grant pages to — the signed token in the path is the whole capability. Sits outside <RequireAuth> in App.tsx for the same reason /login does.",
  },
  {
    route: "/member-view/:token",
    why: "ONE member's own detail page, framed by the Clavis demo for a visitor who has no dashboard login and no staff_allowlist row — the same shape as /trainer-pay/:token, and ungated for the same reason: there is no viewer for the page gate to key on. The signed token in the path IS the capability, and it names the member, so the two reads it unlocks (`/api/members/:id` and its `/conversation`) refuse any id but that one. Sits outside <RequireAuth> in App.tsx.",
  },
  { route: "*", why: "Catch-all redirect to `/`, which is alwaysAllowed." },
];

const PAGE_BY_ID = new Map(DASHBOARD_PAGES.map((p) => [p.id, p]));

export function findPage(id: string): DashboardPage | undefined {
  return PAGE_BY_ID.get(id);
}

/** Ids that are always reachable regardless of a restriction. */
export const ALWAYS_ALLOWED_PAGE_IDS: ReadonlyArray<string> =
  DASHBOARD_PAGES.filter((p) => p.alwaysAllowed === true).map((p) => p.id);

/**
 * Does `granted` permit `pageId`?
 *
 * `granted === null` is UNRESTRICTED — today's behaviour and every row's
 * default. An empty ARRAY is not the same thing: it means "restricted to
 * nothing", and still yields the always-allowed pages. Collapsing the two
 * would make "untick everything" silently mean "grant everything", which is
 * the worst possible reading of that gesture.
 */
export function pageAllowed(
  granted: ReadonlyArray<string> | null,
  pageId: string,
): boolean {
  if (ALWAYS_ALLOWED_PAGE_IDS.includes(pageId)) return true;
  if (granted === null) return true;
  return granted.includes(pageId);
}

/** Strip unknown ids (a page retired since the grant was written). */
export function normalizeVisiblePages(
  raw: ReadonlyArray<string> | null,
): string[] | null {
  if (raw === null) return null;
  const seen = new Set<string>();
  for (const id of raw) if (PAGE_BY_ID.has(id)) seen.add(id);
  return [...seen].sort();
}

/**
 * EVERY page claiming a request URL, most specific first; empty when none
 * does.
 *
 * Longest match wins the ORDERING so `/api/data-counts` is not swallowed by a
 * hypothetical `/api/data` prefix, and an entry NOT ending in `/` must match
 * exactly — the difference between owning `/api/status` and accidentally
 * owning `/api/status-something-else`. But matching does not stop at the
 * winner: a URL can be co-owned (see the `api` field doc), and the gate
 * allows the request when ANY claiming page is granted. The head of the list
 * is the page a denial reports.
 */
export function pagesForApiUrl(url: string): ReadonlyArray<DashboardPage> {
  const path = url.split("?")[0]?.split("#")[0] ?? "";
  const matched: Array<{ page: DashboardPage; len: number }> = [];
  for (const page of DASHBOARD_PAGES) {
    let best = -1;
    for (const entry of page.api) {
      const hit = entry.endsWith("/")
        ? path.startsWith(entry)
        : path === entry;
      if (hit && entry.length > best) best = entry.length;
    }
    if (best >= 0) matched.push({ page, len: best });
  }
  return matched.sort((a, b) => b.len - a.len).map((m) => m.page);
}

/** The single most specific owner, or `null` — `pagesForApiUrl(url)[0]`. */
export function pageForApiUrl(url: string): DashboardPage | null {
  return pagesForApiUrl(url)[0] ?? null;
}

/** True when this `/api` path is deliberately ungated. */
export function isUngatedApi(url: string): boolean {
  const path = url.split("?")[0]?.split("#")[0] ?? "";
  return UNGATED_API.some((u) =>
    u.path.endsWith("/") ? path.startsWith(u.path) : path === u.path,
  );
}

/** `/members/:id` → a regex matching exactly one segment per `:param`. */
function routeToRegExp(route: string): RegExp {
  const escaped = route
    .split("/")
    .map((seg) =>
      seg.startsWith(":")
        ? "[^/]+"
        : seg.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    )
    .join("/");
  return new RegExp(`^${escaped}/?$`);
}

const ROUTE_MATCHERS: ReadonlyArray<{
  readonly page: DashboardPage;
  readonly route: string;
  readonly re: RegExp;
}> = DASHBOARD_PAGES.flatMap((page) =>
  page.routes.map((route) => ({ page, route, re: routeToRegExp(route) })),
);

/**
 * Which page owns an SPA pathname, or `null`.
 *
 * More specific routes win: `/trainers/:id/today` must not be answered by
 * `/trainers/:id`, and `/` must not swallow everything — so matches are ranked
 * by segment count, then by how many segments are literal rather than params.
 */
export function pageForRoute(pathname: string): DashboardPage | null {
  const clean = pathname.split("?")[0]?.split("#")[0] ?? "/";
  let best: DashboardPage | null = null;
  let bestScore = -1;
  for (const m of ROUTE_MATCHERS) {
    if (!m.re.test(clean)) continue;
    const segs = m.route.split("/").filter((s) => s.length > 0);
    const literals = segs.filter((s) => !s.startsWith(":")).length;
    const score = segs.length * 10 + literals;
    if (score > bestScore) {
      best = m.page;
      bestScore = score;
    }
  }
  return best;
}
